import type { PersonId } from '@shared/brand';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { DateRange } from '../value-objects/DateRange';

/**
 * How many people a sub area can spare at once.
 *
 * Every sub area sets a cap on simultaneous absences. This policy answers, for a
 * proposed range, exactly which days would breach that cap and by how much — not
 * merely "yes there is a conflict", because the design shows the offending days
 * and the leader still gets to decide.
 *
 * The cap is deliberately advisory at submission and enforced at approval. A
 * person may file a request that breaches it; the wizard warns and the approval
 * screen shows "2 de 3 personas fuera". Blocking submission outright would leave
 * someone with no way to ask, which is worse than a leader saying no.
 */

/** An absence already on the calendar, reduced to what the cap cares about. */
export interface OccupiedSpan {
  readonly personId: PersonId;
  readonly personName: string;
  readonly range: DateRange;
  /** True while the absence is still awaiting a decision — drawn dotted. */
  readonly tentative: boolean;
}

export interface DayOccupancy {
  readonly date: string;
  readonly peopleOut: number;
  readonly names: readonly string[];
}

export interface OverlapAssessment {
  /** True when at least one day in the proposed range exceeds the cap. */
  readonly exceedsCap: boolean;
  readonly cap: number;
  /** Only the days that breach, in order. Empty when the range is clear. */
  readonly conflictingDays: readonly DayOccupancy[];
  /** The highest simultaneous count reached anywhere in the proposed range. */
  readonly peakPeopleOut: number;
  /** Anyone whose absence touches the proposed range, deduplicated. */
  readonly overlappingPeople: readonly OverlapPeer[];
}

export interface OverlapPeer {
  readonly personId: PersonId;
  readonly personName: string;
  readonly range: DateRange;
  readonly tentative: boolean;
}

/**
 * Assesses a proposed range against the absences already booked in the sub area.
 *
 * `existing` must already exclude the requester's own absences for the request
 * being edited — a request must not conflict with itself when rescheduled. The
 * caller filters, because only it knows which record is being replaced.
 *
 * The cap counts *other* people: a cap of 2 means two colleagues may be out
 * alongside the requester. A day is a breach when the people already out reach
 * the cap, since the requester would make it cap + 1.
 */
export const assessOverlap = (
  proposed: DateRange,
  existing: readonly OccupiedSpan[],
  cap: number,
): OverlapAssessment => {
  const relevant = existing.filter((span) => span.range.overlaps(proposed));

  const conflictingDays: DayOccupancy[] = [];
  let peakPeopleOut = 0;

  for (const day of proposed.days()) {
    const outThatDay = relevant.filter((span) => span.range.contains(day));
    // A person with two overlapping absences must not be counted twice.
    const uniqueNames = [...new Set(outThatDay.map((s) => s.personName))];
    const peopleOut = new Set(outThatDay.map((s) => s.personId)).size;

    peakPeopleOut = Math.max(peakPeopleOut, peopleOut);

    if (peopleOut >= cap) {
      conflictingDays.push({ date: day.toISO(), peopleOut, names: uniqueNames });
    }
  }

  const seen = new Set<string>();
  const overlappingPeople: OverlapPeer[] = [];
  for (const span of relevant) {
    const key = `${span.personId}_${span.range.toString()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    overlappingPeople.push({
      personId: span.personId,
      personName: span.personName,
      range: span.range,
      tentative: span.tentative,
    });
  }

  return {
    exceedsCap: conflictingDays.length > 0,
    cap,
    conflictingDays,
    peakPeopleOut,
    overlappingPeople,
  };
};

/**
 * Occupancy for every day of a window, whether or not it breaches. Feeds the
 * area calendar and the "Cobertura por sub área" progress bars, which show the
 * state of the whole month rather than just the conflicts.
 */
export const occupancyByDay = (
  window: DateRange,
  existing: readonly OccupiedSpan[],
): readonly DayOccupancy[] => {
  const result: DayOccupancy[] = [];
  for (const day of window.days()) {
    const outThatDay = existing.filter((span) => span.range.contains(day));
    result.push({
      date: day.toISO(),
      peopleOut: new Set(outThatDay.map((s) => s.personId)).size,
      names: [...new Set(outThatDay.map((s) => s.personName))],
    });
  }
  return result;
};

/**
 * How a sub area is doing right now, for the coverage card: how many are out
 * today against the cap, and which of the three states the design labels.
 */
export type CoverageState = 'Cubierta' | 'Con gente fuera' | 'En el tope';

export interface Coverage {
  readonly peopleOut: number;
  readonly cap: number;
  readonly state: CoverageState;
  /** 0-1, for the progress bar. Clamped so a breach does not overflow the bar. */
  readonly ratio: number;
}

export const coverageOn = (
  date: CalendarDate,
  existing: readonly OccupiedSpan[],
  cap: number,
): Coverage => {
  const peopleOut = new Set(
    existing.filter((span) => span.range.contains(date)).map((s) => s.personId),
  ).size;

  const state: CoverageState =
    peopleOut === 0 ? 'Cubierta' : peopleOut >= cap ? 'En el tope' : 'Con gente fuera';

  // A cap of zero would divide by zero; treat any absence as full in that case.
  const ratio = cap <= 0 ? (peopleOut > 0 ? 1 : 0) : Math.min(1, peopleOut / cap);

  return { peopleOut, cap, state, ratio };
};

/**
 * Whether a person already has an absence touching this range — the design's
 * "avisos de traslape". Distinct from the cap: this is about the *same* person
 * double-booking themselves, which is always a mistake rather than a judgement
 * call.
 */
export const findSelfOverlap = (
  proposed: DateRange,
  ownAbsences: readonly OccupiedSpan[],
): OccupiedSpan | undefined => ownAbsences.find((span) => span.range.overlaps(proposed));
